import { useCallback, useMemo, useState } from "react";

import { DataTable } from "@/shared/components/DataTable/DataTable";
import SummaryMetricCard from "@/shared/components/SummaryMetricCard";
import { showToast } from "@/shared/lib/toast";
import { closeModal, openModal } from "@/shared/redux/reducers/uiSlice";
import { useAppDispatch } from "@/shared/redux/store/hooks";
import { textFormatter } from "@/shared/utils/textFormatter";
import { CheckCircle2, Clock3, FileText, Wallet } from "lucide-react";

import { App_Url_New } from "@/shared/common/static";
import {
  setCommissionInsurer,
  setCommissionStatuses,
} from "@/shared/redux/reducers/commission-ledger";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { Status } from "../Policies/components/Status";
import GenerateInvoicesModal from "./components/Modal/GenerateInvoicesModal";
import InvoicePrintModal from "./components/Modal/InvoicePrintModal";
import { INVOICE_LOCALISATION } from "./constants/localisation";
import { getInvoiceDetail, getInvoiceRows, getInvoiceSummary } from "./services/mockData";
import type { ApiInvoiceRow, GenerateInvoiceFormValues, InvoiceRecord } from "./types";

const formatCurrency = (amount: number) =>
  `${INVOICE_LOCALISATION.currencySymbol} ${(amount || 0).toLocaleString(
    INVOICE_LOCALISATION.numberLocale
  )}`;

const toNumber = (value: unknown, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const formatDate = (dateValue?: string) => {
  if (!dateValue) return "-";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPeriod = (from?: string, to?: string) => {
  const label = (value?: string) => {
    if (!value) return "";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  };

  const startLabel = label(from);
  const endLabel = label(to);

  if (!startLabel && !endLabel) return "-";
  if (!startLabel || startLabel === endLabel) return endLabel || startLabel;
  if (!endLabel) return startLabel;

  return `${startLabel} - ${endLabel}`;
};

const ALLOWED_STATUSES = new Set(["PAID", "UNBILLED", "RECONCILED", "INVOICED"]);

const mapApiRowToInvoiceRecord = (row: ApiInvoiceRow): InvoiceRecord => {
  const normalizedStatus = String(row.status ?? "").toUpperCase();

  return {
    id: row.id ?? "",
    invoiceNo: String(row.invoice_no ?? "-"),
    insurerId: row.insurer_id ?? "",
    insurer: row.insurer || "-",
    period: formatPeriod(row.period_from, row.period_to),
    periodFrom: row.period_from || "",
    invoiceAmount: toNumber(row.invoice_amount),
    totalCollected: toNumber(row.total_collected),
    outstanding: toNumber(row.outstanding),
    status: (ALLOWED_STATUSES.has(normalizedStatus)
      ? normalizedStatus
      : "UNBILLED") as InvoiceRecord["status"],
    dueDate: row.due_date || "",
    policyNo: String(row.policy_no ?? "-"),
    product: row.product || "-",
    customer: row.customer || "-",
  };
};

export default function InvoicesPage() {
  const dispatch = useAppDispatch();

  const {
    commissionLedger: { statuses, insurers },
  } = usePosterReducers();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiRows = useMemo(() => getInvoiceRows(), []);

  const records = useMemo(() => apiRows.map(mapApiRowToInvoiceRecord), [apiRows]);

  const summary = useMemo(() => getInvoiceSummary(apiRows), [apiRows]);

  const handleGenerateInvoice = useCallback(
    async (values: GenerateInvoiceFormValues, lineCount: number) => {
      setIsSubmitting(true);

      try {
        // TODO(api): POST the draft to the generate-invoice endpoint.
        showToast(
          `Draft for ${values.period} (${lineCount} line(s)) will be saved once the API is live.`,
          "info"
        );
        dispatch(closeModal());
      } catch (error) {
        console.error("Save invoice draft error:", error);
        showToast("Failed to save invoice draft", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch]
  );

  const openGenerateInvoiceModal = useCallback(() => {
    dispatch(
      openModal({
        title: "Generate Invoice",
        children: (
          <GenerateInvoicesModal
            isSubmitting={isSubmitting}
            onSubmit={handleGenerateInvoice}
            onCancel={() => dispatch(closeModal())}
          />
        ),
        className: "max-w-2xl overflow-visible",
        status: "add",
      })
    );
  }, [dispatch, handleGenerateInvoice, isSubmitting]);

  const openInvoicePrintModal = useCallback(
    (row: InvoiceRecord) => {
      const detail = getInvoiceDetail(row.invoiceNo);

      dispatch(
        openModal({
          title: `Invoice ${row.invoiceNo}`,
          children: <InvoicePrintModal detail={detail} onClose={() => dispatch(closeModal())} />,
          className: "max-w-4xl",
          status: "modal",
        })
      );
    },
    [dispatch]
  );

  const summaryCards = useMemo(
    () => [
      {
        label: "Total Invoiced",
        value: formatCurrency(summary.invoiced),
        theme: "primary" as const,
        icon: <FileText className="h-5 w-5" />,
      },
      {
        label: "Collected",
        value: formatCurrency(summary.collected),
        theme: "success" as const,
        icon: <CheckCircle2 className="h-5 w-5" />,
      },
      {
        label: "Outstanding",
        value: formatCurrency(summary.outstanding),
        theme: "warning" as const,
        icon: <Clock3 className="h-5 w-5" />,
      },
      {
        label: "Invoices",
        value: summary.entries,
        theme: "danger" as const,
        icon: <Wallet className="h-5 w-5" />,
      },
    ],
    [summary]
  );

  // --- DataTable Configurations ---

  const tableHeaders = [
    "Invoice No",
    "Insurer",
    "Period",
    "Invoice",
    "Total Collected",
    "Outstanding",
    "Status",
    "Due Date",
  ];

  const tableHeaderKeys = [
    "invoiceNo",
    "insurer",
    "periodFrom",
    "invoiceAmount",
    "totalCollected",
    "outstanding",
    "status",
    "dueDate",
  ];

  const insurerOptionsById = useMemo(
    () =>
      (insurers || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [insurers]
  );

  const statusOptions = useMemo(
    () =>
      (statuses || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [statuses]
  );

  const filters = useMemo(() => {
    return [
      {
        name: "insurer_id",
        valueKey: "insurer_id",
        options: insurerOptionsById,
        placeholder: "Select Insurer",
        requests: {
          method: "GET" as const,
          url: App_Url_New.ApiEndPoint.commissionInsurer,
          reducer: setCommissionInsurer,
        },
        width: "170px",
      },
      {
        name: "status",
        valueKey: "status",
        requests: {
          method: "GET" as const,
          url: App_Url_New.ApiEndPoint.commissionStatus,
          reducer: setCommissionStatuses,
        },
        options: statusOptions,
        placeholder: "Select Status",
        width: "170px",
      },
    ];
  }, [insurerOptionsById, statusOptions]);

  // Provide the custom rendering for the row contents (the <td> elements)
  const renderTableRow = (row: InvoiceRecord) => {
    return (
      <>
        <td className="px-4 py-3 font-medium text-theme-text-soft">{row.invoiceNo}</td>
        <td className="px-4 py-3 max-w-[200px] truncate text-theme-text-soft" title={row.insurer}>
          {row.insurer}
        </td>
        <td className="px-4 py-3 max-w-[160px] truncate text-theme-text-soft" title={row.period}>
          {row.period}
        </td>
        <td className="px-4 py-3 text-center text-theme-text-soft">
          {formatCurrency(row.invoiceAmount)}
        </td>
        <td className="px-4 py-3 text-center text-theme-text-soft">
          {formatCurrency(row.totalCollected)}
        </td>
        <td
          className={`px-4 py-3 text-center ${row.outstanding > 0 ? "text-theme-danger" : "text-theme-text-soft"}`}
        >
          {formatCurrency(row.outstanding)}
        </td>
        <td className="px-4 py-3">
          <Status status={textFormatter(row.status)} />
        </td>
        <td
          className="px-4 py-3 max-w-[160px] truncate text-theme-text-soft"
          title={formatDate(row.dueDate)}
        >
          {formatDate(row.dueDate)}
        </td>
      </>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Summary Metrics */}
      <div className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryCards.map((item) => (
          <SummaryMetricCard
            key={item.label}
            title={item.label}
            value={item.value}
            theme={item.theme}
            icon={item.icon}
          />
        ))}
      </div>

      <DataTable
        headers={tableHeaders}
        headerKeys={tableHeaderKeys}
        rows={records}
        renderRow={renderTableRow}
        onRowClick={openInvoicePrintModal}
        filters={filters}
        button={{
          name: "Generate Invoice",
          onClick: openGenerateInvoiceModal,
        }}
        searchPlaceholder="Search by Invoice No, Insurer, Customer etc..."
        isPagination={true}
        pageSize={10}
        totalCount={records.length}
        sortable={true}
        staticHeight={60}
        nonSortableColumns={["Status"]}
      />
    </div>
  );
}
